import type { OperationalPicture } from '@ddp/shared';

import { apiStream } from '../../api/client.js';

/**
 * Lit le flux SSE de situation (ADR-0040 §6, C4) via `fetch` et appelle `onPicture` à chaque trame.
 *
 * Parseur SSE minimal : les trames sont séparées par une ligne vide (`\n\n`) et seules les lignes
 * `data:` nous intéressent (on ignore `:` commentaires de keep-alive et `event:`/`id:`). La résolution
 * de la promesse signale une fin **propre** du flux (le serveur a fermé) ; un rejet signale une
 * erreur (réseau, 401 non renouvelable, etc.). Dans les deux cas l'appelant décide de retomber en
 * polling et/ou de retenter. L'annulation passe par le `signal` (déconnexion, changement de zone).
 */
export async function readPictureStream(
  zoneId: string,
  onPicture: (picture: OperationalPicture) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await apiStream(`/ops/zones/${zoneId}/stream`, signal);
  if (!response.ok || !response.body) {
    throw new Error(`Flux indisponible (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^ /, ''))
          .join('\n');
        if (!data) continue;
        try {
          onPicture(JSON.parse(data) as OperationalPicture);
        } catch {
          // Trame partielle ou non-JSON (keep-alive) : on l'ignore, la suivante arrivera entière.
        }
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
